import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@powersync/react";
import { progressStatus } from "@ect/contracts";
import { q, recordProgress, formatPercent } from "@ect/core";
import { Button, EmptyState, Icon, Input, normalize, Field, FormSheet, PercentSlider, ProgressBar, StatusChip, Textarea, Timeline } from "@ect/ui";
import { formatDate } from "@ect/core";
import { useSync } from "../app/powersync";

interface ArticleRow { id: string; lot_id: string; lot_name: string; number: number; designation_fr: string; unit: string | null; qty_contract: number | null; pct: number | null; qty_done: number | null; entity_id: string | null }

/** Avancement: lots → articles → PercentSlider → save (build plan sprint 6.2; ≤ 3 taps). */
export function ChantierProgress({ chantierId }: { chantierId: string }) {
  const { t } = useTranslation();
  const { db, actor } = useSync();
  const { data: articles, isLoading } = useQuery<ArticleRow>(q.articlesWithProgress(chantierId).sql, q.articlesWithProgress(chantierId).params);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<ArticleRow | null>(null);
  const [pct, setPct] = useState(0);
  const [comment, setComment] = useState("");
  const [toast, setToast] = useState<string | null>(null);
  const { data: history } = useQuery<{ id: string; pct: number | null; comment: string | null; device_ts: string }>(q.progressHistory(editing?.id ?? "").sql, q.progressHistory(editing?.id ?? "").params);
  const open = (a: ArticleRow) => { setEditing(a); setPct(Math.round(a.pct ?? 0)); setComment(""); };
  const save = async () => {
    if (!editing) return;
    if (editing.pct != null && pct < editing.pct && !comment.trim()) return;
    await recordProgress(db, actor, { articleId: editing.id, chantierId, entityId: editing.entity_id, pct, comment: comment.trim() || undefined });
    setEditing(null);
    setToast(t("chantier.saved"));
    setTimeout(() => setToast(null), 2000);
  };
  if (isLoading) return <p className="text-sm text-ink-3">{t("common.loading")}</p>;
  if (!articles.length) return <EmptyState title={t("chantier.articles")} description="—" />;
  const visible = articles.filter((a) => normalize(`${a.designation_fr} ${a.lot_name} ${a.number}`).includes(normalize(search)));
  const lots = [...new Map(visible.map((a) => [a.lot_id, a.lot_name])).entries()];
  return (
    <div className="grid gap-5">
      <div><h1 className="font-display text-2xl font-extrabold tracking-tight text-navy">{t("chantier.progress")}</h1><p className="mt-1 text-sm text-ink-2">{t("design.progressHint")}</p></div>
      <div className="relative"><Icon name="search" size={18} className="pointer-events-none absolute start-3 top-3.5 text-ink-3" /><Input type="search" aria-label={t("design.searchArticles")} placeholder={t("design.searchArticles")} value={search} onChange={(e) => setSearch(e.target.value)} className="ps-10" /></div>
      {!visible.length && <EmptyState icon={<Icon name="search" />} title={t("design.noResults")} />}
      {toast && <div role="status" className="rounded-md bg-green-soft px-3 py-2 text-sm font-medium text-green">{toast}</div>}
      {lots.map(([lotId, lotName]) => (
        <section key={lotId}>
          <h2 className="mb-3 font-display text-sm font-bold text-ink-2">{lotName}</h2>
          <ul className="grid gap-2">
            {visible.filter((a) => a.lot_id === lotId).map((a) => (
              <li key={a.id}>
                <button type="button" onClick={() => open(a)} className="grid w-full gap-3 rounded-xl border border-line bg-surface p-4 text-start hover:bg-tint">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-ink"><span className="tabular me-2 text-ink-3">{a.number}</span>{a.designation_fr}</span>
                    <StatusChip status={progressStatus(a.pct)} label={t(`status.${progressStatus(a.pct)}`)} />
                  </div>
                  <div className="flex items-center gap-3">
                    <ProgressBar label={t("chantier.progress") + " · " + a.designation_fr} value={a.pct} className="flex-1" />
                    <span className="tabular w-14 text-end font-display text-sm font-bold">{a.pct == null ? "–" : formatPercent(a.pct / 100)}</span>
                  </div>
                  {a.qty_contract != null && <div className="tabular text-xs text-ink-3">{a.qty_done ?? 0} / {a.qty_contract} {a.unit ?? ""}</div>}
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
      <FormSheet open={!!editing} onOpenChange={(o) => !o && setEditing(null)} title={editing?.designation_fr ?? ""} description={editing ? `${editing.lot_name} · ${editing.unit ?? ""}` : undefined} footer={<><Button variant="secondary" onClick={() => setEditing(null)}>{t("common.cancel")}</Button><Button size="lg" onClick={() => void save()}>{t("chantier.save")}</Button></>}>
        <div className="grid gap-5">
          <Field id="pct" label={t("chantier.progress")}><PercentSlider id="pct" value={pct} onChange={setPct} /></Field>
          <Field id="comment" label={t("chantier.comment")} error={editing && editing.pct != null && pct < editing.pct && !comment.trim() ? t("chantier.commentRequired") : undefined}><Textarea id="comment" value={comment} onChange={(e) => setComment(e.target.value)} /></Field>
          {history.length > 0 && <div><div className="mb-2 text-sm font-medium">{t("chantier.history")}</div><Timeline items={history.map((h) => ({ id: h.id, title: `${h.pct ?? "–"} %`, meta: `${formatDate(new Date(h.device_ts))}${h.comment ? ` · ${h.comment}` : ""}`, tone: (h.pct ?? 0) >= 100 ? "good" : "neutral" }))} /></div>}
        </div>
      </FormSheet>
    </div>
  );
}
